import type { Input, Output } from "./call";
import call from "./call";
import useToasts from "@ui/Toast/useToasts";
import React from "react";
import { userMessage } from "./error";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type { EndpointTree } from "../../../service-diagnostics-core/src/Endpoint";

function useCall<M extends keyof EndpointTree, P extends keyof EndpointTree[M]>(
  method: M,
  path: P,
  toastError: boolean = true,
): ((input: Input<M, P>) => Promise<Output<M, P>>) & {
  loading: boolean;
} {
  const { addToast } = useToasts();
  const [loading, setLoading] = React.useState(false);

  const request = React.useCallback(
    async (input: Input<M, P>) => {
      setLoading(true);
      try {
        const result = await call(method, path, input);
        setLoading(false);
        return result;
      } catch (error) {
        const hasBody = error instanceof Error && Boolean(error.message);
        const content = hasBody ? error.message : userMessage(error);
        const title = hasBody ? userMessage(error) : undefined;
        if (toastError) addToast("error", content, title);
        setLoading(false);
        throw error;
      }
    },
    [addToast, method, path, toastError],
  );

  const loadingRequest = request as typeof request & { loading: boolean };
  loadingRequest.loading = loading;

  return loadingRequest;
}

export const usePost = <P extends keyof EndpointTree["POST"]>(path: P, toastError = true) =>
  useCall("POST", path, toastError);
export const usePatch = <P extends keyof EndpointTree["PATCH"]>(path: P, toastError = true) =>
  useCall("PATCH", path, toastError);
export const usePut = <P extends keyof EndpointTree["PUT"]>(path: P, toastError = true) =>
  useCall("PUT", path, toastError);
export const useDelete = <P extends keyof EndpointTree["DELETE"]>(path: P, toastError = true) =>
  useCall("DELETE", path, toastError);
export const useGet = <P extends keyof EndpointTree["GET"]>(path: P, toastError = true) =>
  useCall("GET", path, toastError);
